import React, { FunctionComponent, useState } from 'react';
import { useRouter } from 'next/router';
import { Box, Heading, IconButton, Text, TextButton } from '@wix/design-system';
import ChevronRightIcon from 'wix-ui-icons-common/ChevronRight';
import ChevronLeftIcon from 'wix-ui-icons-common/ChevronLeft';
import MenuSmall from 'wix-ui-icons-common/MenuSmall';
import XIcon from 'wix-ui-icons-common/X';
import { useTranslation } from '@wix/wix-i18n-config';
import { Category } from '@wix/answers-api';
import { DATA_HOOKS } from '../../../dataHooks';
import { BI } from '../../../constants';
import { EventCategoryData } from '../index';
import { InnerLink } from '../../InnerLink';
import { CategoryTreeViewer } from '../../CategoryTreeViewer';
import { isRTL } from '../../../utils/internationalization';
import { HomeIcon } from '../../../icons/HomeIcon';
import css from './index.module.scss';

type CategoryListMobileProps = {
  category: Category;
  subCategory?: Category;
  categories: Category[];
  changeSubCategory: (subCategory: Category) => void;
  changeParentCategory: (category: Category) => void;
  pageClickEvent: (
    clickedItemType: string,
    eventData: EventCategoryData
  ) => Promise<void>;
};

export const CategoryListMobile: FunctionComponent<CategoryListMobileProps> = ({
  category,
  subCategory,
  categories,
  changeSubCategory,
  changeParentCategory,
  pageClickEvent,
}) => {
  const [isOpenCategoriesTree, setOpenCategoriesTree] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const { locale } = useRouter();
  const isRTLLanguage = isRTL(locale);

  const showCategoryTree = async () => {
    setOpenCategoriesTree(true);
    await pageClickEvent(BI.CLICKED_ITEM_TYPES.ALL_TOPICS, {
      clickedText: t('category.all.topics'),
    });
  };

  return (
    <Box direction="vertical" className={css.categorySection}>
      <Box direction="vertical" className={css.categoriesTitleList}>
        <TextButton
          onClick={showCategoryTree}
          className={css.menuButton}
          size="small"
          prefixIcon={<MenuSmall />}
          dataHook={DATA_HOOKS.CATEGORY_MOBILE_MENU_BTN}
        >
          {t('category.all.topics')}
        </TextButton>
        <Box className={css.categoryPath}>
          <InnerLink href="/">
            <HomeIcon />
          </InnerLink>

          {isRTLLanguage ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          <Text
            dataHook={DATA_HOOKS.CATEGORY_MOBILE_TITLE}
            onClick={showCategoryTree}
          >
            {category.name}
          </Text>
        </Box>
        {subCategory && (
          <Heading
            onClick={showCategoryTree}
            className={css.categoryName}
            size="small"
            dataHook={DATA_HOOKS.SUBCATEGORY_MOBILE_TITLE}
          >
            {subCategory.name}
          </Heading>
        )}
      </Box>
      {isOpenCategoriesTree && (
        <div className={css.categoryTreeViewer}>
          <CategoryTreeViewer
            categories={categories}
            currentCategoryId={category.id}
            currentSubCategoryId={subCategory?.id}
            onSubCategoryChange={async (newSubCategory, order) => {
              changeSubCategory(newSubCategory);
              setOpenCategoriesTree(false);
              await pageClickEvent(
                BI.CLICKED_ITEM_TYPES.SUB_CATEGORY_SELECTION,
                {
                  clickedItemId: newSubCategory.id,
                  clickedItemOrder: order,
                  clickedText: newSubCategory.name,
                  clickedUrl: newSubCategory.url,
                }
              );
            }}
            onParentCategoryChange={async (parentCategory, order) => {
              changeParentCategory(parentCategory);
              setOpenCategoriesTree(false);
              await pageClickEvent(BI.CLICKED_ITEM_TYPES.CATEGORY_SELECTION, {
                clickedItemId: parentCategory.id,
                clickedItemOrder: order,
                clickedText: parentCategory.name,
                clickedUrl: parentCategory.url,
              });
            }}
          />
          <IconButton
            className={css.closeButton}
            priority="secondary"
            skin="inverted"
            size="small"
            onClick={() => setOpenCategoriesTree(false)}
          >
            <XIcon />
          </IconButton>
        </div>
      )}
    </Box>
  );
};
